import React, { useEffect, useRef, useState } from "react";

interface ParametersWizardProps {
  onStart: (parameters: any) => void;
  onBack: () => void;
}

export const ParametersWizard: React.FC<ParametersWizardProps> = ({
  onStart: _onStart,
  onBack,
}) => {
  // Mark as used to avoid unused var lint while feature is not implemented
  void _onStart;
  const [parameters, setParameters] = useState({
    vesselMode: "",
    craneFunction: "2.0",
    seaState: "1.0",
    tugger1Position: "Pos 1",
    tugger1Falls: "1",
    tugger2Position: "Pos 1",
    tugger2Falls: "1",
  });

  const [showStartTooltip, setShowStartTooltip] = useState(false);
  const tooltipTimeoutRef = useRef<number | null>(null);
  const [showVerifyTooltip, setShowVerifyTooltip] = useState(false);
  const verifyTooltipTimeoutRef = useRef<number | null>(null);

  const handleInputChange = (field: string, value: string) => {
    setParameters((prev) => ({ ...prev, [field]: value }));
  };

  const handleVerifyParameters = () => {
    setShowVerifyTooltip(true);
    if (verifyTooltipTimeoutRef.current) {
      window.clearTimeout(verifyTooltipTimeoutRef.current);
    }
    verifyTooltipTimeoutRef.current = window.setTimeout(() => {
      setShowVerifyTooltip(false);
      verifyTooltipTimeoutRef.current = null;
    }, 2500);
  };

  const handleStart = () => {
    setShowStartTooltip(true);
    if (tooltipTimeoutRef.current) {
      window.clearTimeout(tooltipTimeoutRef.current);
    }
    tooltipTimeoutRef.current = window.setTimeout(() => {
      setShowStartTooltip(false);
      tooltipTimeoutRef.current = null;
    }, 2500);
  };

  useEffect(() => {
    return () => {
      if (tooltipTimeoutRef.current) {
        window.clearTimeout(tooltipTimeoutRef.current);
      }
      if (verifyTooltipTimeoutRef.current) {
        window.clearTimeout(verifyTooltipTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div className="space-y-6">
      {/* Progress Steps */}
      <div className="flex items-center space-x-4 mb-8">
        <div className="flex items-center space-x-2">
          <div className="w-8 h-8 bg-crane-selection-green rounded-full flex items-center justify-center">
            <span className="text-crane-white text-sm font-medium">✓</span>
          </div>
          <span className="crane-text-body-lg text-crane-white font-medium">
            OPERATION
          </span>
        </div>

        <div className="flex-1 h-px bg-crane-selection-green"></div>

        <div className="flex items-center space-x-2">
          <div className="w-8 h-8 bg-crane-selection-green rounded-full flex items-center justify-center">
            <span className="text-crane-white text-sm font-medium">✓</span>
          </div>
          <span className="crane-text-body-lg text-crane-white font-medium">
            HOISTS
          </span>
        </div>

        <div className="flex-1 h-px bg-crane-selection-green"></div>

        <div className="flex items-center space-x-2">
          <div className="w-8 h-8 bg-crane-selection-blue rounded-full flex items-center justify-center">
            <span className="text-crane-white text-sm font-medium">3</span>
          </div>
          <span className="crane-text-body-lg text-crane-white font-medium">
            PARAMETERS
          </span>
        </div>
      </div>

      {/* Question and Description */}
      <div className="space-y-4">
        <h2 className="crane-text-heading-lg">
          How do you want to set up the crane for this operation?
        </h2>
        <p className="crane-text-body-lg text-crane-gray">
          Fill in the operation parameters so the crane can apply the right
          limits and behavior.
        </p>
      </div>

      {/* General Parameters */}
      <div className="space-y-6">
        <h3 className="crane-text-body-lg font-medium text-crane-white">
          General parameters
        </h3>

        <div className="grid grid-cols-3 gap-4">
          <div className="space-y-2">
            <label className="crane-text-body-md text-crane-gray">
              Vessel mode
            </label>
            <select
              value={parameters.vesselMode}
              onChange={(e) => handleInputChange("vesselMode", e.target.value)}
              className="w-full p-3 bg-crane-darker border border-crane-slate rounded-md text-crane-white crane-text-body-lg"
            >
              <option value="">Select</option>
              <option value="dp">Dynamic Positioning</option>
              <option value="anchored">Anchored</option>
              <option value="transit">Transit</option>
            </select>
          </div>

          <div className="space-y-2">
            <label className="crane-text-body-md text-crane-gray">
              Crane function
            </label>
            <input
              type="text"
              value={parameters.craneFunction}
              onChange={(e) =>
                handleInputChange("craneFunction", e.target.value)
              }
              className="w-full p-3 bg-crane-darker border border-crane-slate rounded-md text-crane-white crane-text-body-lg"
            />
          </div>

          <div className="space-y-2">
            <label className="crane-text-body-md text-crane-gray">
              Sea state
            </label>
            <input
              type="text"
              value={parameters.seaState}
              onChange={(e) => handleInputChange("seaState", e.target.value)}
              className="w-full p-3 bg-crane-darker border border-crane-slate rounded-md text-crane-white crane-text-body-lg"
            />
          </div>
        </div>
      </div>

      {/* Load Tugger Parameters */}
      <div className="grid grid-cols-2 gap-8">
        {/* Load Tugger 1 */}
        <div className="space-y-4">
          <h4 className="crane-text-body-lg font-medium text-crane-white">
            Load tugger 1
          </h4>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="crane-text-body-md text-crane-gray">
                Position
              </label>
              <input
                type="text"
                value={parameters.tugger1Position}
                onChange={(e) =>
                  handleInputChange("tugger1Position", e.target.value)
                }
                className="w-full p-3 bg-crane-darker border border-crane-slate rounded-md text-crane-white crane-text-body-lg"
              />
            </div>

            <div className="space-y-2">
              <label className="crane-text-body-md text-crane-gray">
                Falls
              </label>
              <input
                type="text"
                value={parameters.tugger1Falls}
                onChange={(e) =>
                  handleInputChange("tugger1Falls", e.target.value)
                }
                className="w-full p-3 bg-crane-darker border border-crane-slate rounded-md text-crane-white crane-text-body-lg"
              />
            </div>
          </div>
        </div>

        {/* Load Tugger 2 */}
        <div className="space-y-4">
          <h4 className="crane-text-body-lg font-medium text-crane-white">
            Load tugger 2
          </h4>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="crane-text-body-md text-crane-gray">
                Position
              </label>
              <input
                type="text"
                value={parameters.tugger2Position}
                onChange={(e) =>
                  handleInputChange("tugger2Position", e.target.value)
                }
                className="w-full p-3 bg-crane-darker border border-crane-slate rounded-md text-crane-white crane-text-body-lg"
              />
            </div>

            <div className="space-y-2">
              <label className="crane-text-body-md text-crane-gray">
                Falls
              </label>
              <input
                type="text"
                value={parameters.tugger2Falls}
                onChange={(e) =>
                  handleInputChange("tugger2Falls", e.target.value)
                }
                className="w-full p-3 bg-crane-darker border border-crane-slate rounded-md text-crane-white crane-text-body-lg"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Verify Parameters Button */}
      <div className="pt-4">
        <div className="relative inline-block">
          <button
            onClick={handleVerifyParameters}
            className="crane-button-secondary flex items-center space-x-2"
            aria-describedby={showVerifyTooltip ? "verify-tooltip" : undefined}
          >
            <span>Verify parameters</span>
          </button>
          {showVerifyTooltip && (
            <div
              id="verify-tooltip"
              role="status"
              aria-live="polite"
              className="absolute bottom-full left-0 mb-2 max-w-xs px-3 py-2 rounded-md bg-crane-darker border border-crane-slate text-crane-white crane-text-body-sm shadow-lg z-10"
            >
              This feature is not yet implemented.
              <div className="absolute -bottom-1 left-6 w-2 h-2 bg-crane-darker border-r border-b border-crane-slate rotate-45"></div>
            </div>
          )}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end gap-4 pt-6">
        <button
          onClick={onBack}
          className="crane-button-secondary flex items-center space-x-2"
        >
          <span>←</span>
          <span>Back</span>
        </button>
        <div className="relative">
          <button
            onClick={handleStart}
            className="crane-button-secondary flex items-center space-x-2"
            aria-describedby={showStartTooltip ? "start-tooltip" : undefined}
          >
            <span>✓</span>
            <span>Start</span>
          </button>
          {showStartTooltip && (
            <div
              id="start-tooltip"
              role="status"
              aria-live="polite"
              className="absolute bottom-full right-0 mb-2 max-w-xs px-3 py-2 rounded-md bg-crane-darker border border-crane-slate text-crane-white crane-text-body-sm shadow-lg z-10"
            >
              This feature is not yet implemented.
              <div className="absolute -bottom-1 right-6 w-2 h-2 bg-crane-darker border-r border-b border-crane-slate rotate-45"></div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
